import React, { useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import ApiLink from "../api/ApiLink";
import { useSelector } from "react-redux";
import { useSelectedCompany } from "../context/apiContext/SelectedCompanyContext";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export default function MonthlyWatering() {
  const email = useSelector((state) => state.auth.email);
  const password = useSelector((state) => state.auth.password);
  const { selectedCompanyName } = useSelectedCompany();
  const [chartsData, setChartsData] = useState([]);

  useEffect(() => {
    const fetchChartsData = async () => {
      try {
        const response = await ApiLink.post("/api/get-charts-data", {
          email: email,
          password: password,
          company_id: selectedCompanyName.company_id,
        });

        if (response.status === 200) {
          setChartsData(response.data.Data);
        }
      } catch (error) {
        console.error("Error:", "There is currently no charts data available");
      }
    };
    fetchChartsData();
  }, [email, password, selectedCompanyName]);
  const mapChartData = (data) => {
    const allDates = new Set();
    const currentMonth = new Date().getMonth() + 1;
    const currentYear = new Date().getFullYear();

    Object.values(data).forEach((location) => {
      Object.keys(location.data).forEach((date) => {
        const [year, month] = date.split("-");
        allDates.add(date);
      });
    });

    const sortedDates = Array.from(allDates).sort();

    const excludeMonthCount = 1;
    const labels = sortedDates.slice(excludeMonthCount);

    const solidColors = [
      "rgba(54, 162, 235, 0.8)", // Light Blue
      "rgba(255, 159, 64, 0.8)", // Orange
      "rgba(75, 192, 192, 0.8)", // Light Green
      "rgba(128, 128, 128, 0.8)", // Dark Gray
      "rgba(173, 216, 230, 0.8)", // Light Cyan
      "rgba(255, 99, 132, 0.8)", // Red
      "rgba(255, 105, 180, 0.8)", // Pink/Magenta
      "rgba(153, 102, 255, 0.8)", // Purple
      "rgba(255, 206, 86, 0.8)", // Yellow
      "rgba(199, 199, 199, 0.8)", // Light Gray
      "rgba(83, 102, 255, 0.8)", // Blue
      "rgba(204, 255, 144, 0.8)", // Bright Green
      "rgba(255, 127, 80, 0.8)", // Coral
    ];

    const borderColors = solidColors.map((color) => color.replace("0.8", "1"));

    const datasets = Object.keys(data).map((key, index) => {
      const location = data[key];
      const locationData = location.data || {};
      const dataset = labels.map((date) => {
        const savings = locationData[date]?.savings || 0;
        return {
          x: date,
          y: 100 - savings,
          actualSavings: savings,
          name: locationData[date]?.name,
          xSprinkled: locationData[date]?.x,
          ySprinkled: locationData[date]?.y,
          companyName: location.company_location_name,
        };
      });

      return {
        label: location.company_location_name,
        backgroundColor: solidColors[index % solidColors.length],
        borderColor: borderColors[index % borderColors.length],
        borderWidth: 1,
        hoverBackgroundColor: solidColors[index % solidColors.length].replace(
          "0.8",
          "1"
        ),
        hoverBorderColor: borderColors[index % borderColors.length],
        data: dataset,
      };
    });

    return { labels, datasets };
  };

  const data = mapChartData(chartsData);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          padding: 15,
          usePointStyle: true,
          pointStyle: "circle",
          font: {
            size: 12,
          },
        },
      },
      title: {
        display: true,
        text: "Monthly Waterings Per Locations",
        font: {
          size: 18,
          weight: "bold",
        },
        color: "gray",
        padding: {
          top: 10,
          bottom: 20,
        },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const { dataset, dataIndex } = context;
            const dataPoint = dataset.data[dataIndex];
            return [
              `${dataPoint.companyName}`,
              `Sprinkled: ${dataPoint.xSprinkled} out of ${dataPoint.ySprinkled} times`,
              `Savings: ${dataPoint.actualSavings}%`,
            ];
          },
        },
        usePointStyle: true,
        boxWidth: 8,
        boxHeight: 8,
        titleFont: {
          size: 13,
        },
        bodyFont: {
          size: 12,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        title: {
          display: true,
          text: "Watering Per Location",
          font: {
            size: 12,
          },
        },
        ticks: {
          font: {
            size: 10,
          },
          stepSize: 25,
        },
        grid: {
          color: "rgba(0, 0, 0, 0.1)",
          lineWidth: 0.5,
        },
      },
      x: {
        ticks: {
          font: {
            size: 10,
          },
          maxRotation: 0,
          minRotation: 0,
        },
        grid: {
          display: false,
        },
      },
    },
    layout: {
      padding: {
        top: 20,
        bottom: 10,
      },
    },
    elements: {
      bar: {
        borderWidth: 0.5,
        borderRadius: 2,
        barThickness: 8,
        maxBarThickness: 10,
      },
    },
    interaction: {
      intersect: true,
      mode: "nearest",
    },
  };

  return (
    <div className="chart-container w-full" style={{ height: "400px" }}>
      <Bar data={data} options={options} />
    </div>
  );
}
